/**
 * ZXing-C++ WASM decoder for robust barcode/Data Matrix decoding.
 *
 * This wraps the `zxing-wasm` package (ZXing-C++ compiled to WebAssembly)
 * which has significantly better Data Matrix support than the Java-based
 * ZXing port used by html5-qrcode.
 *
 * The module is dynamically imported so the ~963KB WASM binary only loads
 * when the scanner is actually opened, not on initial page load.
 *
 * Key advantages over html5-qrcode for Data Matrix:
 * - Native C++ ZXing implementation (not a JS port of the Java version)
 * - Built-in tryInvert for inverted/worn codes
 * - Built-in tryRotate for rotated codes
 * - Built-in tryDownscale for resolution adaptation
 * - Proper binarizer options for varying lighting conditions
 */
import type { ReaderOptions } from 'zxing-wasm/reader';

// Lazy-loaded module reference — only imported when first decode is attempted
type ReadBarcodesFn = (typeof import('zxing-wasm/reader'))['readBarcodes'];
let readBarcodesLazy: ReadBarcodesFn | null = null;
let loadPromise: Promise<void> | null = null;

async function ensureLoaded(): Promise<ReadBarcodesFn> {
  if (readBarcodesLazy) return readBarcodesLazy;
  if (!loadPromise) {
    loadPromise = import('zxing-wasm/reader').then((mod) => {
      readBarcodesLazy = mod.readBarcodes;
    });
  }
  await loadPromise;
  return readBarcodesLazy!;
}

/** Options focused on Data Matrix decoding with maximum robustness */
const DM_READER_OPTIONS: ReaderOptions = {
  formats: ['DataMatrix'],
  tryHarder: true,
  tryRotate: true,
  tryInvert: true,
  tryDownscale: true,
  tryDenoise: true,
  maxNumberOfSymbols: 1,
};

/** Options for all supported barcode formats */
const ALL_FORMATS_READER_OPTIONS: ReaderOptions = {
  formats: [
    'DataMatrix',
    'QRCode',
    'Code128',
    'Code39',
    'EAN13',
    'EAN8',
    'UPCA',
    'UPCE',
  ],
  tryHarder: true,
  tryRotate: true,
  tryInvert: true,
  tryDownscale: true,
  maxNumberOfSymbols: 1,
};

/**
 * Decode a barcode from ImageData using ZXing-C++ WASM.
 * Tries Data Matrix first (with aggressive settings), then all formats.
 */
export async function decodeWithZxingWasm(
  imageData: ImageData,
): Promise<string | null> {
  try {
    const readBarcodes = await ensureLoaded();

    // Try DM-specific decode first with aggressive settings
    const dmResults = await readBarcodes(imageData, DM_READER_OPTIONS);
    for (const result of dmResults) {
      if (result.isValid && result.text) {
        console.log('[ZxingWasm] Decoded DataMatrix:', result.format);
        return result.text;
      }
    }

    // Fallback: try all formats
    const allResults = await readBarcodes(imageData, ALL_FORMATS_READER_OPTIONS);
    for (const result of allResults) {
      if (result.isValid && result.text) {
        console.log('[ZxingWasm] Decoded:', result.format);
        return result.text;
      }
    }
  } catch (error) {
    console.warn('[ZxingWasm] Decode error:', error);
  }
  return null;
}

/**
 * Decode focusing only on Data Matrix with maximum robustness settings.
 * Used for cropped regions where the ML model has already identified a DM.
 */
export async function decodeDataMatrixWithZxingWasm(
  imageData: ImageData,
): Promise<string | null> {
  try {
    const readBarcodes = await ensureLoaded();

    const results = await readBarcodes(imageData, {
      ...DM_READER_OPTIONS,
      // For cropped regions, also try different binarizers
      binarizer: 'LocalAverage',
    });
    for (const result of results) {
      if (result.isValid && result.text) {
        return result.text;
      }
    }

    // Retry with GlobalHistogram binarizer for different lighting
    const results2 = await readBarcodes(imageData, {
      ...DM_READER_OPTIONS,
      binarizer: 'GlobalHistogram',
    });
    for (const result of results2) {
      if (result.isValid && result.text) {
        return result.text;
      }
    }
  } catch (error) {
    console.warn('[ZxingWasm] DM decode error:', error);
  }
  return null;
}
